import React, { useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { feeSchedulePrograms as fspUtils, TrackerContext } from '@unite-us/client-utils';
import AddCirclePlusButton from 'common/display/Profile/components/AddCirclePlusButton';
import callOrLog from 'common/utils/callOrLog';
import FeatureFlagContainer from 'src/common/utils/FeatureFlags/FeatureFlagContainer';
import { SERVICES_PROVIDED } from 'common/utils/EventTracker/utils/eventConstants';
import { CREATE_FEE_SCHEDULE_PROVIDED_SERVICE } from 'src/components/Cases/constants';
import hideInteractions from 'src/components/Facesheet/Cases/utils/hideElement';
import { isEmpty, get, findIndex, flow } from 'lodash';
import { isCaseOpen } from 'src/components/Cases/utils';
import { fetchProvidedServices } from 'actions/Case/Contact/Group';
import { useFindRecord, usePopulate } from 'api/APIHooks';
import { Spinner } from 'common/spinners';
import ProvidedServiceCards from './cards/ProvidedServiceCards';
import NoProvidedServiceCard from './cards/NoProvidedServiceCard';
import ProvidedServiceFormWrapper from './form/ProvidedServiceFormWrapper';
import ProvidedServiceForm from './form/ProvidedServiceForm';

function ProvidedServices({
  caseId,
  className,
  contactId,
  editProvidedServiceId,
  editing,
  feeScheduleProvidedServices,
  fetchProvidedServices: fetchProvidedServiceAction,
  groupId,
  isCoordinationGroup,
  scrollInto,
  serviceAuthorization,
  serviceCase,
}) {
  const programId = get(serviceCase, 'program.id');
  const { data: programData } = useFindRecord(
    'program',
    programId,
    { queryConfig: { placeholderData: undefined, enabled: !!programId } },
  );
  const program = programData?.data?.data;
  usePopulate(
    'fee_schedule_program',
    'fee_schedule_program',
    program,
    { queryConfig: { placeholderData: undefined, enabled: !!program } },
  );
  const feeScheduleProgram = program?.fee_schedule_program;
  const paymentType = feeScheduleProgram?.payment_type;
  const isCostType = feeScheduleProgram && fspUtils.isCostPaymentType(paymentType);

  const [isFeeScheduleClicked, setClick] = useState(false);
  const [isEditing, setEditMode] = useState(editing);
  const [scroll, setScroll] = useState(scrollInto);
  const [editedProvidedServiceId, setProvidedServiceEditId] = useState(editProvidedServiceId);
  const metafields = get(serviceCase, 'program.fee_schedule_program.metafields', []);
  const authStatus = serviceAuthorization.state;
  const hasNonApprovedAuth = !!authStatus && (authStatus !== 'approved');

  const canAddServices = isCaseOpen(serviceCase);
  const eventTracker = useContext(TrackerContext);
  const showServicesProvidedInteractions = !hideInteractions({ isCoordinationGroup, serviceCase, groupId });
  const [isFetching, setFetchingState] = useState(true);

  const createAddFeeScheduleServicesHandler = useCallback(() => {
    if (!isFeeScheduleClicked) {
      callOrLog(() => eventTracker(SERVICES_PROVIDED.addedNewFeeScheduleServiceProvided));
    }
    setClick(!isFeeScheduleClicked);
  }, [isFeeScheduleClicked]);

  const updateProvidedServicesHandler = useCallback((providedServiceIdSelected) => {
    setEditMode(providedServiceIdSelected !== '');
    setProvidedServiceEditId(providedServiceIdSelected);
  }, []);

  const fetchPrograms = () => {
    async function fetchPS() {
      const returned = await fetchProvidedServiceAction({ caseId, groupId, contactId });
      if (isEditing && scroll && returned.status === 200) {
        const element = document.getElementsByClassName('payments-track-service-card--edit')[0];
        if (element !== undefined) {
          element.scrollIntoView();
          setScroll(false);
        }
      }
      setFetchingState(false);
    }
    fetchPS();
  };

  useEffect(
    () => {
      fetchPrograms();
    },
    [fetchProvidedServices, isEditing,
      editedProvidedServiceId, setEditMode, setProvidedServiceEditId],
  );

  const authToolTip =
    'You can only add a Contracted Service to the case if the authorization request is accepted.';

  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-4">
        <h2>Contracted Service</h2>
        <div>
          {
            canAddServices && showServicesProvidedInteractions ? (
              <AddCirclePlusButton
                addHandler={createAddFeeScheduleServicesHandler}
                id="add-fee-schedule-service-provided-button"
                labelText="Add New Contracted Service"
                disabled={isFeeScheduleClicked || isEditing || hasNonApprovedAuth}
                toolTip={hasNonApprovedAuth ? authToolTip : ''}
              />
            ) : null
          }
        </div>
      </div>
      {
        !isFeeScheduleClicked && isFetching && (<Spinner />)
      }
      {
        !isFeeScheduleClicked && !isFetching && (
          (isEmpty(feeScheduleProvidedServices) ? (<NoProvidedServiceCard />) :
            (
              <ProvidedServiceCards
                contactId={contactId}
                feeScheduleProvidedServices={feeScheduleProvidedServices}
                editMode={updateProvidedServicesHandler}
                isEditing={isEditing}
                metafields={metafields}
                serviceCase={serviceCase}
                program={program}
                feeScheduleProgram={feeScheduleProgram}
                selectedProvidedServiceId={editedProvidedServiceId}
                showServicesProvidedInteractions={showServicesProvidedInteractions}
              />
            )))
      }
      {
        isFeeScheduleClicked && (
          <ProvidedServiceFormWrapper
            contactId={contactId}
            serviceCase={serviceCase}
          >
            <ProvidedServiceForm
              isBillable={get(feeScheduleProgram, 'billable')}
              cancelFunction={() => setClick(!isFeeScheduleClicked)}
              contactId={contactId}
              isCostType={isCostType}
              serviceCase={serviceCase}
              formName={CREATE_FEE_SCHEDULE_PROVIDED_SERVICE}
              paymentType={paymentType}
            />
          </ProvidedServiceFormWrapper>
        )
      }
    </div>
  );
}

ProvidedServices.propTypes = {
  caseId: PropTypes.string,
  className: PropTypes.string,
  contactId: PropTypes.string,
  editProvidedServiceId: PropTypes.string,
  editing: PropTypes.bool,
  feeScheduleProvidedServices: PropTypes.array,
  fetchProvidedServices: PropTypes.func.isRequired,
  groupId: PropTypes.string,
  isCoordinationGroup: PropTypes.bool.isRequired,
  scrollInto: PropTypes.bool,
  serviceAuthorization: PropTypes.object,
  serviceCase: PropTypes.shape({
    id: PropTypes.string,
    contact: PropTypes.object,
  }).isRequired,
};

ProvidedServices.defaultProps = {
  caseId: '',
  className: '',
  contactId: '',
  editProvidedServiceId: '',
  editing: false,
  feeScheduleProvidedServices: [],
  groupId: '',
  scrollInto: false,
  serviceAuthorization: {},
};

function mapStateToProps(state, ownProps) {
  const { contactId, serviceCase } = ownProps;
  const serviceCases = get(state, `serviceCase.${contactId}`);
  const index = findIndex(serviceCases, (serviceCaseItem) => serviceCaseItem.id === serviceCase.id);

  const feeScheduleProvidedServices = get(state, `serviceCase.${contactId}[${index}].feeScheduleProvidedServices`);

  return {
    feeScheduleProvidedServices,
  };
}

export default flow(
  FeatureFlagContainer,
  connect(mapStateToProps, {
    fetchProvidedServices,
  }),
)(ProvidedServices);
